import { motion } from "framer-motion";
import { Smartphone, Bell, Zap, BadgeCheck } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { IMAGES } from "./data";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.karm.industries.order";
const APP_STORE_URL = "https://apps.apple.com/in/app/pellets-buy/id6809854719";

const FEATURES = [
  { icon: Zap, text: "Order pellets in a few taps" },
  { icon: Bell, text: "Daily rate alerts on your phone" },
  { icon: BadgeCheck, text: "Track your bulk supply orders" },
];

const GooglePlayIcon = () => (
  <svg viewBox="0 0 24 24" className="h-7 w-7 text-emerald-400" fill="currentColor" aria-hidden="true">
    <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065 3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z" />
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor" aria-hidden="true">
    <path d="M17.05 12.54c-.03-2.89 2.36-4.27 2.47-4.34-1.35-1.97-3.44-2.24-4.18-2.27-1.78-.18-3.47 1.05-4.37 1.05-.9 0-2.29-1.02-3.77-1-1.94.03-3.72 1.13-4.72 2.86-2.01 3.49-.51 8.66 1.45 11.49.96 1.39 2.1 2.95 3.6 2.89 1.45-.06 2-.93 3.74-.93s2.24.93 3.77.9c1.56-.03 2.55-1.41 3.5-2.81 1.1-1.61 1.55-3.17 1.58-3.25-.04-.02-3.03-1.16-3.07-4.59zM14.16 4.06c.8-.97 1.34-2.32 1.19-3.66-1.15.05-2.55.77-3.38 1.73-.74.86-1.39 2.23-1.22 3.55 1.29.1 2.6-.65 3.41-1.62z"/>
  </svg>
);

export const DownloadApp = () => (
  <section id="app" data-testid="download-app-section" className="relative overflow-hidden py-20 lg:py-32">
    <div className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-500/10 blur-3xl" />
    <div className="container relative mx-auto grid grid-cols-1 items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
      <Reveal className="order-2 flex justify-center lg:order-1">
        <motion.div
          whileHover={{ rotate: -2, scale: 1.03 }}
          transition={{ type: "spring", stiffness: 200, damping: 14 }}
          className="glow-border relative rounded-[2.5rem] border border-emerald-500/30 p-3"
        >
          <img
            src={IMAGES.appIcon}
            alt="PELLETS BUY app by KARM INDUSTRIES — order bio pellets from your phone"
            className="w-64 rounded-[2rem] sm:w-80"
            loading="lazy"
            data-testid="app-icon-image"
          />
          <span className="absolute -right-3 -top-3 rounded-full bg-emerald-500 px-4 py-2 font-mono2 text-[11px] font-bold uppercase tracking-widest text-[#050B07] shadow-lg">
            New
          </span>
        </motion.div>
      </Reveal>

      <Reveal delay={0.12} className="order-1 lg:order-2">
        <Eyebrow>Our App</Eyebrow>
        <h2 className="font-display mt-4 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="download-app-heading">
          PELLETS BUY — Download Now
        </h2>
        <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
          Order KARM bio pellets straight from your phone. Check today&apos;s rates,
          place bulk orders and track dispatch — all inside the PELLETS BUY app.
        </p>

        <div className="mt-7 space-y-3">
          {FEATURES.map((f) => (
            <div key={f.text} className="flex items-center gap-3 text-sm text-slate-300">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-950 text-emerald-400">
                <f.icon className="h-4 w-4" />
              </span>
              {f.text}
            </div>
          ))}
        </div>

        <div className="mt-9 flex flex-wrap gap-4">
          <motion.a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="play-store-button"
            whileHover={{ y: -4 }}
            className="flex items-center gap-3 rounded-2xl border border-emerald-500/40 bg-[#0B150F] px-6 py-3.5 text-white transition-colors duration-300 hover:border-emerald-400 hover:bg-[#101F16]"
          >
            <GooglePlayIcon />
            <span className="text-left">
              <span className="block text-[10px] uppercase tracking-widest text-slate-400">Get it on</span>
              <span className="font-display block text-lg font-bold leading-tight">Google Play</span>
            </span>
          </motion.a>

          <motion.a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="app-store-button"
            whileHover={{ y: -4 }}
            className="flex items-center gap-3 rounded-2xl border border-emerald-500/40 bg-[#0B150F] px-6 py-3.5 text-white transition-colors duration-300 hover:border-emerald-400 hover:bg-[#101F16]"
          >
            <AppleIcon />
            <span className="text-left">
              <span className="block text-[10px] uppercase tracking-widest text-slate-400">Download on the</span>
              <span className="font-display block text-lg font-bold leading-tight">App Store</span>
            </span>
          </motion.a>
        </div>

        <p className="mt-5 flex items-center gap-2 text-xs text-slate-500">
          <Smartphone className="h-4 w-4 text-emerald-500" />
          Free download · Available on Android and iOS
        </p>
      </Reveal>
    </div>
  </section>
);
